// Картата на един сервитьор на телефон. Общата транспозиция „ред на колона"
// би направила от 8+ колони карта от 8 реда; тук първият ред носи това, което
// човек търси — кой и колко, — а разбивката по методи стои под него.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import { formatPercent } from "../../../../components/dataTable";
import type { MethodEntry } from "../../../../utils/paymentMethodsDisplay";
import type { WaiterRow } from "./waitersMath";

/** Минус знак U+2212 — отстъпката и сторното са отнета сума. */
const MINUS = "−";

type Props = {
  row: WaiterRow;
  methodEntries: MethodEntry[];
  showDiscounts: boolean;
  showVoids: boolean;
};

export const WaiterMobileCard = ({
  row,
  methodEntries,
  showDiscounts,
  showVoids,
}: Props) => {
  const { t } = useTranslation();

  // Метод без пари при ТОЗИ сервитьор не заема място в картата — на телефон
  // редицата нули изтласква надолу това, което се чете.
  const usedMethods = methodEntries.filter(
    (method) => Math.abs(row.byMethod[method.id] ?? 0) >= 0.005,
  );
  const hasDeductions =
    (showDiscounts && row.discounts >= 0.005) ||
    (showVoids && row.voids >= 0.005);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-ink">
          {row.waiterName}
        </span>
        <span className="font-pos-mono text-[15px] font-bold tabular-nums text-ink">
          {fmtEuro(row.total)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] font-medium text-muted">
        <span className="font-pos-mono tabular-nums">
          {t("reportsPage.waitersBillsMany", { count: row.bills })}
        </span>
        <span aria-hidden>·</span>
        <span className="font-pos-mono tabular-nums">
          {fmtEuro(row.average)}
        </span>
        <span aria-hidden>·</span>
        <span className="font-pos-mono tabular-nums">
          {formatPercent(row.share, 1)}
        </span>
      </div>

      {usedMethods.length ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] font-medium text-soft">
          {usedMethods.map((method, index) => (
            <span key={method.id} className="flex items-center gap-2">
              {index > 0 ? <span aria-hidden>·</span> : null}
              <span>
                {method.label}{" "}
                <span className="font-pos-mono font-bold tabular-nums text-ink">
                  {fmtEuro(row.byMethod[method.id] ?? 0)}
                </span>
              </span>
            </span>
          ))}
        </div>
      ) : null}

      {hasDeductions ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] font-medium text-danger-ink">
          {showDiscounts && row.discounts >= 0.005 ? (
            <span>
              {t("reportsPage.discountsReport")}{" "}
              <span className="font-pos-mono font-bold tabular-nums">
                {MINUS}
                {fmtEuro(row.discounts)}
              </span>
            </span>
          ) : null}
          {showVoids && row.voids >= 0.005 ? (
            <span>
              {t("reportsPage.cancellations")}{" "}
              <span className="font-pos-mono font-bold tabular-nums">
                {MINUS}
                {fmtEuro(row.voids)}
              </span>
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
